/**
 * sRGB 채널 값(0-255)을 선형 값으로 변환
 */
function linearize(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/**
 * WCAG 상대 휘도 기반으로 배경색에 대비되는 텍스트 색상 결정
 */
export function getContrastTextColor(bgHex: string): "#ffffff" | "#1f2937" {
  const hex = bgHex.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const luminance =
    0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);

  return luminance > 0.179 ? "#1f2937" : "#ffffff";
}

/**
 * Hex → HSL 변환
 */
function hexToHsl(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return [0, 0, l];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let hue = 0;
  if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) hue = ((b - r) / d + 2) / 6;
  else hue = ((r - g) / d + 4) / 6;

  return [hue * 360, s, l];
}

/**
 * HSL → Hex 변환
 */
function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h / 360 + 1 / 3);
    g = hue2rgb(p, q, h / 360);
    b = hue2rgb(p, q, h / 360 - 1 / 3);
  }

  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * 카테고리 뱃지용 스타일 생성
 * 원본 색상에서 연한 틴트 배경 + 같은 색조의 진한 텍스트를 자동 도출
 */
export function getBadgeStyle(hex: string): {
  backgroundColor: string;
  color: string;
} {
  const [h, s, l] = hexToHsl(hex);

  // 회색 계열 (채도가 매우 낮은 경우)
  if (s < 0.1) {
    return {
      backgroundColor: hslToHex(h, s, Math.max(l, 0.92)),
      color: hslToHex(h, s, Math.min(l, 0.25)),
    };
  }

  // 이미 밝은색 (100/200단계)이면 배경을 그대로 쓰고 텍스트만 진하게
  const bgLightness = l > 0.7 ? l : 0.92;
  const textLightness = l < 0.3 ? l : 0.28;

  return {
    backgroundColor: hslToHex(h, Math.min(s, 0.85), bgLightness),
    color: hslToHex(h, Math.min(s * 0.9, 0.75), textLightness),
  };
}

/**
 * 색조(hue) × 명도(shade) 팔레트
 * 각 행: 밝은색 → 어두운색 (6단계)
 * 열: 10가지 색조
 */
export const COLOR_PALETTE = [
  // red        orange      yellow      green       teal        blue        indigo      violet      pink        gray
  [
    "#FEE2E2",
    "#FFEDD5",
    "#FEF9C3",
    "#DCFCE7",
    "#CCFBF1",
    "#DBEAFE",
    "#E0E7FF",
    "#EDE9FE",
    "#FCE7F3",
    "#F3F4F6",
  ], // 100 (가장 밝음)
  [
    "#FECACA",
    "#FED7AA",
    "#FDE68A",
    "#BBF7D0",
    "#99F6E4",
    "#BFDBFE",
    "#C7D2FE",
    "#DDD6FE",
    "#FBCFE8",
    "#D1D5DB",
  ], // 200
  [
    "#F87171",
    "#FB923C",
    "#FBBF24",
    "#4ADE80",
    "#2DD4BF",
    "#60A5FA",
    "#818CF8",
    "#A78BFA",
    "#F472B6",
    "#9CA3AF",
  ], // 400
  [
    "#EF4444",
    "#F97316",
    "#EAB308",
    "#22C55E",
    "#14B8A6",
    "#3B82F6",
    "#6366F1",
    "#8B5CF6",
    "#EC4899",
    "#6B7280",
  ], // 500 (기본)
  [
    "#DC2626",
    "#EA580C",
    "#CA8A04",
    "#16A34A",
    "#0D9488",
    "#2563EB",
    "#4F46E5",
    "#7C3AED",
    "#DB2777",
    "#4B5563",
  ], // 600
  [
    "#991B1B",
    "#9A3412",
    "#854D0E",
    "#166534",
    "#115E59",
    "#1E40AF",
    "#3730A3",
    "#5B21B6",
    "#9D174D",
    "#374151",
  ], // 800 (가장 어두움)
];

/** 팔레트 열 수 */
export const PALETTE_COLS = COLOR_PALETTE[0].length;
